// Pin and group images for the map, painted in the image worker (paint.worker.ts) so the page's main thread stays
// free while the map draws; on the page itself where a browser can't (no OffscreenCanvas, or the worker fails).
import { cssColor } from "./draw";
import { drawGroup, drawPin, PAINT_TOKENS, type Face, type PinImage } from "./paint";
import type { Look } from "../shared/rules";

type Job = { kind: "pin"; emoji: string; diameter: number; style: Look } | { kind: "group"; face: Face; count: number; style: Look };

const paintHere = (j: Job): PinImage => (j.kind === "pin" ? drawPin(j.emoji, j.diameter, j.style) : drawGroup(j.face, j.count, j.style));

let worker: Worker | null = null, nextJob = 0;
const waiting = new Map<number, { job: Job; resolve: (img: PinImage) => void }>();

function start(): Worker | null {
  if (worker || typeof Worker === "undefined" || typeof OffscreenCanvas === "undefined") return worker;
  try {
    worker = new Worker(new URL("./paint.worker.ts", import.meta.url), { type: "module" });
  } catch { return null; }
  worker.postMessage({ colors: Object.fromEntries(PAINT_TOKENS.map((t) => [t, cssColor(t)])) });
  worker.onmessage = (ev: MessageEvent<{ job: number; image?: PinImage; error?: string }>) => {
    const w = waiting.get(ev.data.job);
    if (!w) return;
    waiting.delete(ev.data.job);
    // A worker that can't paint (no emoji font there, say): this one on the page.
    w.resolve(ev.data.image ?? paintHere(w.job));
  };
  worker.onerror = (ev) => {
    // The worker broke (or its load was cut off by the page leaving): everything waiting, and everything after, is
    // painted on the page. Handled here, so the browser doesn't also report it as the page's own error.
    ev.preventDefault();
    worker = null; brokenWorker = true;
    for (const [id, w] of waiting) { waiting.delete(id); w.resolve(paintHere(w.job)); }
  };
  return worker;
}
let brokenWorker = false;

/** Starts the worker ahead of the first image (the map calls it when it's created), so it's loaded by then. */
export function warmUp() { if (!brokenWorker) start(); }

export function paint(job: Job): Promise<PinImage> {
  const w = brokenWorker ? null : start();
  if (!w) return Promise.resolve(paintHere(job));
  return new Promise((resolve) => {
    const id = ++nextJob;
    waiting.set(id, { job, resolve });
    w.postMessage({ ...job, job: id });
  });
}
